import React, { useEffect, useState } from 'react'
import { UseRangeProps, useRange } from 'react-instantsearch'

import { round } from 'lodash'

import { useTranslate } from 'hooks'

import css from './range-input-customized.module.scss'

const unsetNumberInputValue = ''

type RangeInputCustomizedProps = {
  precision?: number
  rangeFactor?: number
} & UseRangeProps

const RangeInputCustomized = ({ precision = 0, rangeFactor = 1, ...props }: RangeInputCustomizedProps) => {
  const { start, range, canRefine, refine } = useRange({ ...props, precision })
  const { t } = useTranslate('strapi-search-[...term]')

  const step = 1 / Math.pow(10, precision || 0)
  const minWithFactor = round((range.min ?? 0) * rangeFactor, 0)
  const maxWithFactor = round((range.max ?? 0) * rangeFactor, 0)

  const [rangeState, setRangeState] = useState({
    from: '',
    to: '',
  })

  useEffect(() => {
    // init: set value from url params
    const min = start[0] !== -Infinity && start[0] !== range.min ? (start[0] ?? 0) * rangeFactor : unsetNumberInputValue
    const max = start[1] !== Infinity && start[1] !== range.max ? (start[1] ?? 0) * rangeFactor : unsetNumberInputValue

    setRangeState({
      from: min ? round(Number(min), 0).toString() : '',
      to: max ? round(Number(max), 0).toString() : '',
    })
  }, [rangeFactor, range.min, range.max, start])

  const { from, to } = rangeState

  const adjustedMinWithFactor = minWithFactor > 1 ? minWithFactor - 1 : 0
  const isRangeRevered = !!rangeState.to && !!rangeState.from && Number(rangeState.to) <= Number(rangeState.from)
  const isFromOutOfRange = !!rangeState.from && Number(rangeState.from) < adjustedMinWithFactor
  const isToOutOfRange = !!rangeState.to && Number(rangeState.to) > maxWithFactor + 1
  return (
    <form className={css.container}>
      <label>
        <input
          className={css.rangeInput}
          type="number"
          min={adjustedMinWithFactor}
          max={maxWithFactor + 1}
          value={stripLeadingZeroFromInput(from || unsetNumberInputValue)}
          step={step}
          placeholder={adjustedMinWithFactor.toString()}
          disabled={!canRefine}
          onInput={({ currentTarget }) => {
            const value = currentTarget.value
            setRangeState({ from: value || unsetNumberInputValue, to })
          }}
        />
      </label>
      <span className={css.separator}>to</span>
      <label>
        <input
          className={css.rangeInput}
          type="number"
          min={adjustedMinWithFactor}
          max={maxWithFactor + 1}
          value={stripLeadingZeroFromInput(to || unsetNumberInputValue)}
          step={step}
          placeholder={(maxWithFactor + 1).toString()}
          disabled={!canRefine}
          onInput={({ currentTarget }) => {
            const value = currentTarget.value
            setRangeState({ from, to: value || unsetNumberInputValue })
          }}
        />
      </label>

      <button
        className={css.btnSubmit}
        disabled={isRangeRevered || isFromOutOfRange || isToOutOfRange}
        onClick={(event) => {
          event.preventDefault()

          let adjustedFromWithoutFactor = from ? Number(from) / rangeFactor : undefined
          let adjustToWithoutFactor = to ? Number(to) / rangeFactor : undefined

          if (Number(adjustedFromWithoutFactor) < Number(range.min)) {
            adjustedFromWithoutFactor = range.min
          }

          if (Number(adjustToWithoutFactor) > Number(range.max)) {
            adjustToWithoutFactor = range.max
          }

          refine([adjustedFromWithoutFactor, adjustToWithoutFactor])
        }}
      >
        {t('PageSearch.PageSearch_Filters_action_go')}
      </button>
    </form>
  )
}

function stripLeadingZeroFromInput(value: string): string {
  return value.replace(/^(0+)\d/, (part) => Number(part).toString())
}

export default RangeInputCustomized
